import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { FaTimes, FaChevronLeft, FaChevronRight, FaExpand } from 'react-icons/fa';
import assetUrl from '../../services/assetUrl';
import './trainersShowcase.css';

const EASE = [0.22, 1, 0.36, 1];

// Only the BUNDLED placeholder cards (shipped in the repo's public folder under
// /torii_trainers/) are served raw from the build. Everything the admin uploads —
// full S3 URLs, `/images/uploads/..` keys, or `\uploads\..` — goes through
// assetUrl, which maps onto the S3 bucket / API host correctly.
const resolveImg = (src) => {
  if (!src) return '';
  const s = String(src).replace(/\\/g, '/');
  if (s.includes('/torii_trainers/')) return encodeURI(s); // bundled placeholder → serve raw
  return assetUrl(s);
};

const altFor = (t) => `${t.name || 'Torii technical trainer'}${t.role ? ` — ${t.role}` : ''}`;

// NOTE: TrainerCard / TrainerRibbon are declared at MODULE scope (and memoized)
// on purpose. If they were defined inside TrainersShowcase, every parent re-render
// (the Placements page auto-rotates its testimonials, etc.) would give them a new
// component identity → React would remount them → the CSS marquee would restart
// from the top and never scroll past the first couple of cards.
const TrainerCard = React.memo(({ item, idx, dup, onOpen }) => (
  <button
    type="button"
    className="plx-trainers__card"
    onClick={(e) => onOpen(idx, e)}
    aria-hidden={dup ? 'true' : undefined}
    tabIndex={dup ? -1 : 0}
    aria-label={dup ? undefined : `View ${altFor(item)}`}
  >
    <span className="plx-trainers__frame">
      <img src={resolveImg(item.image)} alt={dup ? '' : altFor(item)} loading="lazy" />
      <span className="plx-trainers__view" aria-hidden="true"><FaExpand /> View</span>
    </span>
  </button>
));

// A vertical auto-scrolling column (up or down). Items are duplicated so the
// marquee loops seamlessly; the copies are hidden from a11y / tab order.
const TrainerRibbon = React.memo(({ row, dir, onOpen }) => (
  <div className={`plx-vribbon plx-vribbon--${dir}`}>
    <div className="plx-vribbon__track" style={{ animationDuration: `${Math.max(20, row.length * 6)}s` }}>
      {[...row, ...row].map((entry, k) => (
        <TrainerCard key={k} item={entry.item} idx={entry.idx} dup={k >= row.length} onOpen={onOpen} />
      ))}
    </div>
  </div>
));

const TrainersShowcase = ({ trainers = {} }) => {
  const reduce = useReducedMotion();
  const items = (trainers.items || []).filter((t) => t && t.image);

  const [open, setOpen] = useState(null); // active index into `items`, or null
  const openerRef = useRef(null);         // the card that opened the lightbox
  const closeRef = useRef(null);
  const panelRef = useRef(null);

  // Stable identity so the memoized ribbons never re-render (keeps the marquee running).
  const openAt = useCallback((i, e) => { openerRef.current = e?.currentTarget || null; setOpen(i); }, []);
  const close = useCallback(() => setOpen(null), []);
  const prev = useCallback(() => setOpen((i) => (i == null ? i : (i - 1 + items.length) % items.length)), [items.length]);
  const next = useCallback(() => setOpen((i) => (i == null ? i : (i + 1) % items.length)), [items.length]);

  // Keyboard (Esc / arrows / Tab-trap) + scroll lock while the lightbox is open.
  useEffect(() => {
    if (open == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      else if (e.key === 'Tab') {
        const nodes = panelRef.current?.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])');
        if (!nodes || !nodes.length) return;
        const list = Array.from(nodes);
        const first = list[0];
        const last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => closeRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
    };
  }, [open, close, prev, next]);

  // Return focus to the card that opened the lightbox once it closes.
  useEffect(() => {
    if (open == null && openerRef.current) {
      openerRef.current.focus?.();
      openerRef.current = null;
    }
  }, [open]);

  if (!items.length) return null;

  // Split into two vertical columns (even/odd), keeping each card's original
  // index so clicking any copy opens the right lightbox slide.
  const colA = items.map((item, idx) => ({ item, idx })).filter((_, i) => i % 2 === 0);
  const colB = items.map((item, idx) => ({ item, idx })).filter((_, i) => i % 2 === 1);

  const active = open != null ? items[open] : null;

  return (
    <section className="placements-section placements-section--center plx-trainers">
      <div className="placements-section__head">
        {trainers.eyebrow && <span className="placements-eyebrow">{trainers.eyebrow}</span>}
        <h2 className="placements-section__title">
          {trainers.title || 'Meet Our'} {trainers.highlight && <span>{trainers.highlight}</span>}
        </h2>
        {trainers.intro && <p className="plx-lead">{trainers.intro}</p>}
      </div>

      <div className="plx-trainers__split">
        {/* Left — architects team image */}
        <motion.div
          className="plx-trainers__hero"
          initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.985 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          {trainers.teamBanner && (
            <img src={resolveImg(trainers.teamBanner)} alt="Claude Certified Architects — the Torii technical team" loading="lazy" />
          )}
        </motion.div>

        {/* Right — two vertical sliders gliding opposite ways */}
        <div className="plx-trainers__vcols" aria-label="Technical trainers">
          <TrainerRibbon row={colA} dir="up" onOpen={openAt} />
          {colB.length > 0 && <TrainerRibbon row={colB} dir="down" onOpen={openAt} />}
        </div>
      </div>

      {createPortal(
        <AnimatePresence>
          {active && (
            <motion.div
              className="plx-lb"
              role="dialog"
              aria-modal="true"
              aria-label={active.name || 'Trainer'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={close}
            >
              <motion.div
                className="plx-lb__stage"
                ref={panelRef}
                onClick={(e) => e.stopPropagation()}
                initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.97, transition: { duration: 0.16 } }}
                transition={{ duration: 0.25, ease: EASE }}
              >
                <button type="button" className="plx-lb__close" ref={closeRef} onClick={close} aria-label="Close">
                  <FaTimes />
                </button>

                <img className="plx-lb__img" src={resolveImg(active.image)} alt={altFor(active)} />

                {active.specialization && <p className="plx-lb__cap">{active.specialization}</p>}

                {items.length > 1 && (
                  <>
                    <button type="button" className="plx-lb__nav plx-lb__nav--prev" onClick={prev} aria-label="Previous trainer">
                      <FaChevronLeft />
                    </button>
                    <button type="button" className="plx-lb__nav plx-lb__nav--next" onClick={next} aria-label="Next trainer">
                      <FaChevronRight />
                    </button>
                    <span className="plx-lb__count">{open + 1} / {items.length}</span>
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </section>
  );
};

export default TrainersShowcase;
